import {
  BlockType,
  ExperienceListDto,
  ExperienceModule,
} from "@intelligems/ig-types";

import {
  ExperienceCategoryTab,
  ExperienceModuleFilter,
  ModuleStatusFilter,
} from "./experienceModuleConfig";

type StatusCircleColor = "primary" | "secondary" | "warning" | "success";

export const getExperienceTypeLabel = (experience: ExperienceListDto) =>
  experience.category === "experiment" ? "Test" : "Always-on";

export const getExperienceRoutePath = (experience: ExperienceListDto) => {
  const tab = experience.status === "pending" ? "mods" : "analytics";
  const typeParam =
    experience.category === "experiment" ? `&type=${experience.type}` : "";
  const root =
    experience.category === "experiment" ? "/experiment" : "/personalization";

  return `${root}/${experience.id}?action=edit${typeParam}&tab=${tab}`;
};

export const getExperienceStatusLabel = (
  experience: ExperienceListDto
): ModuleStatusFilter => {
  if (experience.archivedAtTs) {
    return "Archived";
  }

  if (experience.status === "started") {
    return "Active";
  }
  if (experience.status === "paused") {
    return "Paused";
  }
  if (experience.status === "ended") {
    return "Ended";
  }
  if (experience.hasSchedule) {
    return "Scheduled";
  }

  return "Pending";
};

export const getExperienceStatusColor = (
  experience: ExperienceListDto
): StatusCircleColor => {
  if (experience.archivedAtTs || experience.status === "pending") {
    return "secondary";
  }

  if (experience.status === "started") {
    return "success";
  }

  if (experience.status === "paused") {
    return "warning";
  }

  return "secondary";
};

const hasCartDiscount = (
  experience: ExperienceListDto,
  method: "perItem" | "perOrder"
) =>
  experience.variations.some(
    (variation) =>
      variation.offerDiscountApplicationType === "cartDiscount" &&
      variation.offerDiscountApplicationMethod === method
  );

const hasOfferDiscountType = (
  experience: ExperienceListDto,
  discountType: "tieredDiscount" | "freeGift" | "freeShipping" | "buyXGetY"
) =>
  experience.variations.some(
    (variation) => variation.offerDiscountApplicationType === discountType
  );

const hasCheckoutBlockType = (
  experience: ExperienceListDto,
  blockType: BlockType
) =>
  experience.variations.some((variation) =>
    variation.checkoutBlockTypes?.includes(blockType)
  );

export const getExperienceModificationFilterLabels = (
  experience: ExperienceListDto
) => {
  const labels: ExperienceModuleFilter[] = [];

  if (experience.testTypes.hasTestContentOnsite) {
    labels.push("Content Edits");
  }
  if (experience.testTypes.hasTestContentUrl) {
    labels.push("Redirects");
  }
  if (experience.testTypes.hasTestOnsiteInjections) {
    labels.push("Styles & JavaScript");
  }
  if (experience.testTypes.hasTestContentTheme) {
    labels.push("Theme Change");
  }
  if (experience.testTypes.hasTestContentTemplate) {
    labels.push("Template Changes");
  }

  if (experience.testTypes.hasTestCampaign) {
    if (hasCartDiscount(experience, "perItem")) {
      labels.push("Amount off Products");
    }
    if (hasCartDiscount(experience, "perOrder")) {
      labels.push("Amount off Order");
    }
    if (hasOfferDiscountType(experience, "tieredDiscount")) {
      labels.push("Volume Discount");
    }
    if (hasOfferDiscountType(experience, "freeGift")) {
      labels.push("Free Gift");
    }
    if (hasOfferDiscountType(experience, "buyXGetY")) {
      labels.push("Buy X Get Y");
    }
  }
  if (experience.testTypes.hasTestOnsiteUpsell) {
    labels.push("Onsite Upsell");
  }
  if (experience.testTypes.hasTestPricing) {
    labels.push("Price Change");
  }
  if (experience.testTypes.hasTestShipping) {
    labels.push("Shipping Rates");
  }
  if (hasOfferDiscountType(experience, "freeShipping")) {
    labels.push("Free Shipping");
  }

  if (experience.testTypes.hasTestCheckoutBlocks) {
    if (hasCheckoutBlockType(experience, "upsell")) {
      labels.push("Upsell");
    }
    if (hasCheckoutBlockType(experience, "trustBadge")) {
      labels.push("Trust Badge");
    }
    if (hasCheckoutBlockType(experience, "timer")) {
      labels.push("Countdown Timer");
    }

    if (hasCheckoutBlockType(experience, "shippingProgressBar")) {
      labels.push("Shipping Bar");
    }
  }
  if (experience.testTypes.hasTestPostPurchase) {
    labels.push("Post-purchase Upsell");
  }
  return labels;
};

const matchesSearchInput = (
  experience: ExperienceListDto,
  searchInput: string
) => {
  const normalizedSearchInput = searchInput.trim().toLowerCase();
  if (!normalizedSearchInput) {
    return true;
  }

  return (
    experience.name.toLowerCase().includes(normalizedSearchInput) ||
    experience.id.toLowerCase().includes(normalizedSearchInput)
  );
};

const matchesCategoryTab = (
  experience: ExperienceListDto,
  categoryTab: ExperienceCategoryTab
) => {
  if (categoryTab === "all") {
    return true;
  }
  return categoryTab === "tests"
    ? experience.category === "experiment"
    : experience.category !== "experiment";
};

const matchesStatusFilters = (
  experience: ExperienceListDto,
  statusFilters: ModuleStatusFilter[]
) =>
  statusFilters.length === 0 ||
  statusFilters.includes(getExperienceStatusLabel(experience));

const matchesModificationFilters = (
  experience: ExperienceListDto,
  modificationFilters: ExperienceModuleFilter[]
) => {
  if (modificationFilters.length === 0) {
    return true;
  }

  const labels = getExperienceModificationFilterLabels(experience);
  return modificationFilters.some((filter) => labels.includes(filter));
};

export const filterExperienceModuleRows = ({
  experiences,
  searchInput,
  categoryTab,
  statusFilters,
  modificationFilters,
}: {
  experiences: ExperienceListDto[];
  searchInput: string;
  categoryTab: ExperienceCategoryTab;
  statusFilters: ModuleStatusFilter[];
  modificationFilters: ExperienceModuleFilter[];
}) =>
  experiences.filter(
    (experience) =>
      matchesSearchInput(experience, searchInput) &&
      matchesCategoryTab(experience, categoryTab) &&
      matchesStatusFilters(experience, statusFilters) &&
      matchesModificationFilters(experience, modificationFilters)
  );
